/*
 * Adding documents to a referral from the portal (TDI-578 SENCO, TDI-579
 * parent or carer, TDI-580 closing at send).
 *
 * The rules live on the server (python-functions/constants/rms_referral_uploads.py,
 * enforced by rms_referral_document_portal.py). What is here is the screen's
 * side: checking a form before it is sent, so a person hears about a missing
 * label or a file that is too big without waiting for an upload, and turning
 * the server's refusal reasons into words that say what to do next. The
 * server checks everything again and is the only authority.
 *
 * Wording follows Diana's Documents frames (Figma 3613:46095, D-01, D-04,
 * D-05, D-06). Bracketed placeholders in the frames are implemented without
 * brackets and listed for Jasmine's comprehension gate.
 */

// 20 MB, PDF or Word: the server's MAX_FILE_BYTES and FILE_KIND_MIME.
export const MAX_FILE_BYTES = 20 * 1024 * 1024;
export const ACCEPTED_EXTENSIONS = ['pdf', 'doc', 'docx'];
export const FILE_INPUT_ACCEPT = [
  '.pdf', '.doc', '.docx',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
].join(',');
// The server's DESCRIPTION_MAX_CHARS.
export const DESCRIPTION_MAX_CHARS = 200;

/** "20 MB" from a byte count, for the hint and the size message. */
export function sizeLimitLabel(maxBytes = MAX_FILE_BYTES) {
  const mb = Math.round((Number(maxBytes) || MAX_FILE_BYTES) / (1024 * 1024));
  return `${mb} MB`;
}

/** The hint under the Document label (frame: "PDF format only. [Size limit]"). */
export function fileHint(maxBytes = MAX_FILE_BYTES) {
  return `PDF or Word, up to ${sizeLimitLabel(maxBytes)}`;
}

export const DATE_HINT = 'If you do not know the day, enter the month and year, or just the year.';

export const DOCUMENTS_UNTIL_SENT =
  'You can add documents until the referral is sent. After that, documents can’t be added or removed.';
export const DOCUMENTS_SENT =
  'The referral has been sent, so documents can no longer be added or removed. The hospital team can see the documents you added.';
export const DOCUMENTS_SENT_NONE =
  'The referral has been sent, so documents can no longer be added.';
export const DOCUMENTS_NONE_YET = 'Not added yet';
export const DOCUMENTS_NONE_SENT = 'No documents were added to this referral.';
export const DOCUMENTS_LOAD_FAILED = 'Your documents could not be loaded. Check your connection and try again.';

export const ERROR_SUMMARY_TITLE = 'Check the details below';
export const ERROR_SUMMARY_BODY = 'Fix the fields marked with an error message, then try again.';

export const COULD_NOT_ADD_TITLE = 'The document couldn’t be added';
export function couldNotAddBody(maxBytes = MAX_FILE_BYTES) {
  return `Try again. If it still doesn’t work, check that the file is a PDF or Word document and smaller than ${sizeLimitLabel(maxBytes)}.`;
}

export const COULD_NOT_REMOVE_TITLE = 'The document couldn’t be removed';
export const COULD_NOT_REMOVE_BODY = 'Check your connection and try again.';

export const REMOVE_TITLE = 'Remove this document?';
export function removeBody(description) {
  return `${description || 'This document'} will be deleted, and the hospital team won’t see it.`;
}
export const REMOVE_CONFIRM = 'Yes, remove it';

export function addedNotice(description) {
  return `${description || 'Your document'} has been added.`;
}
export function removedNotice(description) {
  return `${description || 'The document'} has been removed.`;
}

/** The count line on the phone record's Documents row: "1 document". */
export function documentsCountLabel(count) {
  const n = Number(count) || 0;
  if (n === 0) return DOCUMENTS_NONE_YET;
  return n === 1 ? '1 document' : `${n} documents`;
}

/**
 * The parent or carer's cap, said before they start and as they go
 * (TDI-579): "You can add up to 5 documents. You have 3 left." Null for a
 * side with no cap (the school).
 */
export function capLine(info) {
  const { cap, remaining } = info || {};
  if (typeof cap !== 'number' || typeof remaining !== 'number') return null;
  const upTo = `You can add up to ${cap} ${cap === 1 ? 'document' : 'documents'}.`;
  if (remaining >= cap) return upTo;
  if (remaining <= 0) return CAP_REACHED;
  return `${upTo} You have ${remaining} left.`;
}
export const CAP_REACHED =
  'You have added the most documents you can for this referral. Remove one to add another.';

// Before the parent or carer has agreed to the referral, adding is locked
// (TDI-786, R-046): the server's consent_not_given refusal, same wording.
export const ADD_LOCKED_UNTIL_CONSENT =
  'You can add documents once you’ve agreed to the referral.';

// ── Field messages (frames D-01 and P12) ────────────────────────────────────

export const FIELD_MESSAGES = {
  file_missing: 'Choose a file to add',
  file_type: 'This file is not a PDF or Word document. Choose a PDF, .doc or .docx file.',
  file_too_large: null, // built from the limit, see fieldMessage
  file_empty: 'This file is empty. Choose the file again.',
  description_missing: 'Tell us what this document is',
  description_too_long: `Describe the document in ${DESCRIPTION_MAX_CHARS} characters or fewer`,
  document_type_missing: 'Choose a document type',
  date_missing: 'Enter the date of the document',
  date_not_valid: 'Enter a real date that is not in the future, or just the year',
};

export function fieldMessage(reason, maxBytes = MAX_FILE_BYTES) {
  if (reason === 'file_too_large') {
    return `This file is bigger than ${sizeLimitLabel(maxBytes)}. Choose a smaller file.`;
  }
  return FIELD_MESSAGES[reason] || null;
}

function extensionOf(name) {
  const s = String(name || '').trim().toLowerCase();
  return s.includes('.') ? s.split('.').pop() : '';
}

/**
 * Why this file cannot be sent, or null. A quick check on the name and size
 * only: the server also reads the file's first bytes, so a renamed file is
 * still refused there.
 */
export function fileRefusal(file, maxBytes = MAX_FILE_BYTES) {
  if (!file) return 'file_missing';
  if (!ACCEPTED_EXTENSIONS.includes(extensionOf(file.name))) return 'file_type';
  if (!file.size) return 'file_empty';
  if (file.size > maxBytes) return 'file_too_large';
  return null;
}

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * The date a document covers, from the three boxes: a year alone, a month
 * and year, or a full date (TDI-578, "the date or year it covers").
 * Returns { value: 'YYYY' | 'YYYY-MM' | 'YYYY-MM-DD' } or { error: reason }.
 * Same rule as valid_date_covered on the server: a real date, from 1900,
 * not in the future (a year or month counts from its first day).
 */
export function dateCoveredFromParts({ day, month, year } = {}, today = new Date()) {
  const d = String(day || '').trim();
  const m = String(month || '').trim();
  const y = String(year || '').trim();
  if (!d && !m && !y) return { error: 'date_missing' };
  if (!/^\d{4}$/.test(y)) return { error: 'date_not_valid' };
  if (d && !m) return { error: 'date_not_valid' };
  if ((m && !/^\d{1,2}$/.test(m)) || (d && !/^\d{1,2}$/.test(d))) return { error: 'date_not_valid' };
  const yearN = Number(y);
  const monthN = m ? Number(m) : 1;
  const dayN = d ? Number(d) : 1;
  if (monthN < 1 || monthN > 12 || dayN < 1) return { error: 'date_not_valid' };
  // Built in UTC from its parts so no time zone moves the day.
  const start = new Date(Date.UTC(yearN, monthN - 1, dayN));
  if (start.getUTCMonth() !== monthN - 1 || start.getUTCDate() !== dayN) return { error: 'date_not_valid' };
  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  if (yearN < 1900 || start.getTime() > todayUtc) return { error: 'date_not_valid' };
  if (!m) return { value: y };
  if (!d) return { value: `${y}-${pad2(monthN)}` };
  return { value: `${y}-${pad2(monthN)}-${pad2(dayN)}` };
}

/** Every type id an organisation offers, from the server's grouped list. */
export function documentTypeIds(groups) {
  const ids = [];
  (groups || []).forEach((g) => (g.types || []).forEach((t) => ids.push(t.id)));
  return ids;
}

/**
 * Check the whole form before it is sent. Returns { errors, dateCovered }:
 * errors is keyed by field (file, description, documentType, date) in the
 * form's order, each a reason code; empty when the form can be sent.
 */
export function validateDocumentForm(
  { file, description, documentType, date } = {},
  { maxBytes = MAX_FILE_BYTES, typeIds = null, today = new Date() } = {},
) {
  const errors = {};
  const fileReason = fileRefusal(file, maxBytes);
  if (fileReason) errors.file = fileReason;
  const text = String(description || '').trim();
  if (!text) errors.description = 'description_missing';
  else if (text.length > DESCRIPTION_MAX_CHARS) errors.description = 'description_too_long';
  const type = String(documentType || '').trim();
  if (!type || (Array.isArray(typeIds) && typeIds.length > 0 && !typeIds.includes(type))) {
    errors.documentType = 'document_type_missing';
  }
  const parsed = dateCoveredFromParts(date, today);
  if (parsed.error) errors.date = parsed.error;
  return { errors, dateCovered: parsed.value || null };
}

// The server's refusal reasons that belong to one field.
const FIELD_FOR_REASON = {
  file_type: 'file',
  file_too_large: 'file',
  file_empty: 'file',
  description_missing: 'description',
  document_type_missing: 'documentType',
  date_not_valid: 'date',
};

// Refusals that are about the referral, not the form: the server's own
// plain-English message is shown as it is.
export const REFERRAL_REFUSAL_MESSAGES = {
  referral_sent: 'The referral has been sent, so documents can no longer be added or removed.',
  cap_reached: CAP_REACHED,
  not_on_referral_pathway: 'Documents can only be added to a referral.',
  not_an_uploader: 'Only the school and the parent or carer can add documents to this referral.',
};

function errorReason(error) {
  const details = (error && error.details) || {};
  return typeof details.reason === 'string' ? details.reason : null;
}

/**
 * What an add that the server refused means for the screen:
 *   { field, reason }   a field error (the form shows it under that field)
 *   { notice: { title, body } }   a referral-level refusal or a failed upload
 */
export function addFailure(error, maxBytes = MAX_FILE_BYTES) {
  const reason = errorReason(error);
  if (reason && FIELD_FOR_REASON[reason]) return { field: FIELD_FOR_REASON[reason], reason };
  if (reason && REFERRAL_REFUSAL_MESSAGES[reason]) {
    return { notice: { title: COULD_NOT_ADD_TITLE, body: REFERRAL_REFUSAL_MESSAGES[reason] } };
  }
  // A withdrawn referral (TDI-475) is refused with its own message.
  if (error && error.details && error.details.consentWithdrawn && error.message) {
    return { notice: { title: COULD_NOT_ADD_TITLE, body: error.message } };
  }
  return { notice: { title: COULD_NOT_ADD_TITLE, body: couldNotAddBody(maxBytes) } };
}

/** The body of the "could not remove" notice for a refused removal. */
export function removeFailureBody(error) {
  const reason = errorReason(error);
  if (reason === 'referral_sent') return REFERRAL_REFUSAL_MESSAGES.referral_sent;
  if (reason === 'not_your_document') return 'You can only remove documents you added.';
  return COULD_NOT_REMOVE_BODY;
}

/** The file as base64 with no data-URL prefix, as rmsAddReferralDocument takes it. */
export function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('The file could not be read'));
    reader.readAsDataURL(file);
  });
}
